/**
 * The patient dashboard's lens prescribing module, above lens dispensing.
 *
 * Lists the patient's spectacle and contact lens prescriptions - which is current, which has been
 * superseded, has expired or was withdrawn - with each one's powers. A draft opens in the prescription
 * dialog for its kind; an issued or withdrawn prescription is locked and doesn't open. "Dispense" opens
 * the dispensing job dialog with the prescription already on the job; "Renew" opens a new prescription
 * repeating it; one found to be wrong is withdrawn from its row. A contact lens draft on trial shows
 * when its review is due, and "Trial" opens the trial dialog - which, like the prescription dialog, can
 * hand on to the other when it closes.
 *
 * Shown only to a practice with the lensDispensingFeature, which the API also requires.
 */
app.component("lensPrescriptionsTile", {
	templateUrl: "app/src/js/components/lensPrescribing/lensPrescriptionsTile.html",
	controller: "lensPrescriptionsTileController",
	bindings: {
		patientId: "<",
	},
});

app.controller("lensPrescriptionsTileController", [
	"$scope",
	"$mdDialog",
	"$element",
	"$animate",
	"LensPrescribingStore",
	"LensDispensingStore",
	"Session",
	"toast",
	function ($scope, $mdDialog, $element, $animate, Rx, Store, Session, toast) {
		const $ctrl = this;
		$animate.enabled($element, false);
		$scope.R = Rx;
		$scope.S = Store;
		// Which kind a new prescription is - asked when the add link is clicked.
		$scope.picker = { open: false };
		$scope.state = { loading: false, error: null };
		$scope.prescriptions = [];

		const features = (Session.practice && Session.practice.practiceFeatures) || {};
		$scope.enabled = !!features.lensDispensingFeature && Session.hasPermission("CLINICAL_LENS_DISPENSE");
		// Dispensers see and dispense prescriptions; only prescribers write or delete them.
		$scope.canPrescribe = Session.hasPermission("CLINICAL_LENS_PRESCRIBE");

		function load() {
			if (!$ctrl.patientId || !$scope.enabled) return;
			$scope.prescriptions = Rx.forPatient($ctrl.patientId);
			$scope.state = { loading: true, error: null };
			Rx.load($ctrl.patientId)
				.catch(function (resp) {
					$scope.state.error = Store.errorMessage(resp, "The prescriptions could not be loaded.");
				})
				.finally(function () {
					$scope.state.loading = false;
				});
		}
		$ctrl.$onChanges = load;
		$scope.reload = load;

		$scope.status = (p) => Rx.statusOf($ctrl.patientId, p);
		$scope.statusClass = (p) => "rx-" + $scope.status(p).key;
		$scope.jobCount = (p) => Rx.jobsFor($ctrl.patientId, p).length;
		$scope.currentCount = () => $scope.prescriptions.filter((p) => $scope.status(p) === Rx.STATUS.CURRENT).length;

		function openDialog(dialog, locals) {
			return $mdDialog.show({
				controller: dialog.controller,
				templateUrl: dialog.templateUrl,
				parent: angular.element(document.body),
				clickOutsideToClose: false,
				escapeToClose: false,
				scope: $scope.$new(true),
				preserveScope: false,
				locals: locals,
			});
		}

		// The prescription dialogs hand on to each other, one open at a time: a contact lens prescription
		// closes with { next: "trial" } to have its trial opened, and the trial with { next: "rx", step } to
		// have the prescription opened again at the step its outcome needs.
		function followOn(result) {
			if (!result || !result.next) return;
			if (result.next === "trial") $scope.openTrial(result.prescription, null, result.newTrial);
			else if (result.next === "rx") $scope.openPrescription(result.prescription, null, result.step);
		}

		// `type` picks the kind for a new prescription; a draft opens in its own, at `step` if given. Only a
		// draft is opened: an issued or withdrawn prescription is locked - the row shows it, and a change is
		// a new prescription. Only prescribers write them.
		$scope.canOpen = (p) => $scope.canPrescribe && (!p || Rx.isDraft(p));
		$scope.openPrescription = function (p, type, step) {
			$scope.picker.open = false;
			if (!$scope.canOpen(p)) return;
			openDialog(Rx.dialogFor(p, type), { existingRx: p || null, startStep: step || null }).then(followOn, angular.noop);
		};

		// A contact lens draft's trial: the pair that is out, or - `newTrial` - another one going home.
		$scope.openTrial = function (p, $event, newTrial) {
			if ($event) $event.stopPropagation();
			if (!$scope.canPrescribe) return;
			openDialog(Rx.trialDialog, { existingRx: p, newTrial: !!newTrial }).then(followOn, angular.noop);
		};
		// The current trial's review is due today or has passed.
		$scope.trialDue = function (p) {
			const trial = Rx.onTrial(p) && Rx.currentTrial(p);
			return !!trial && !trial.outcome && !!trial.review && moment(trial.review).isSameOrBefore(moment(), "day");
		};

		// A new prescription repeating an issued one - same lens and powers, a new expiry - opened to
		// check against today's findings and issue. The usual end of a contact lens aftercare.
		$scope.renew = function (p, $event) {
			if ($event) $event.stopPropagation();
			if (!$scope.canPrescribe || !Rx.isIssued(p)) return;
			const draft = Rx.renewal(p);
			openDialog(Rx.dialogFor(draft), { existingRx: draft, startStep: null }).then(followOn, angular.noop);
		};

		// A dispensing job with this prescription already on it.
		$scope.dispense = function (p, $event) {
			if ($event) $event.stopPropagation();
			if (!Rx.isUsable(p)) return;
			const job = Rx.newJob(p);
			openDialog(Store.dialogFor(job), { existingJob: job, jobPatient: null }).catch(angular.noop);
		};

		// An issued prescription found to be wrong is withdrawn from here, with a reason - it isn't opened.
		$scope.withdraw = function (p, $event) {
			if ($event) $event.stopPropagation();
			if (!Rx.isIssued(p) || !Rx.canWithdraw()) return;
			openDialog(Rx.withdrawDialog, { existingRx: p, patientId: $ctrl.patientId }).catch(angular.noop);
		};

		// The patient's copy of an issued prescription, to print or download.
		$scope.patientCopy = function (p, $event) {
			if ($event) $event.stopPropagation();
			Rx.openPatientCopy(p).catch((resp) => toast.create({ timeout: 6000, message: Store.errorMessage(resp, "The patient's copy could not be made."), className: "alert-danger", dismissible: true }));
		};

		$scope.remove = function (p, $event) {
			if ($event) $event.stopPropagation();
			if (p.$removing) return;
			p.$removing = true;
			Rx.remove($ctrl.patientId, p)
				.catch((resp) => toast.create({ timeout: 6000, message: Store.errorMessage(resp, "The draft could not be deleted."), className: "alert-danger", dismissible: true }))
				.finally(() => (p.$removing = false));
		};
	},
]);
